/**
 * @fileoverview The org dashboard's Today card: every class meeting on today,
 * in the order they start, each opening its class page with that meeting open
 * — where the Calendar's "Open class" goes.
 *
 * It sits beside the sessions chart and takes the chart's height, never more:
 * a busy day scrolls inside the card. The filters are the program types the
 * organization runs (lib/orgToday), quiet text tabs over the list. A meeting
 * that has ended reads quieter than one still to come, one under way says
 * Now, and a cancelled one stays in its place as "No class", as on the
 * Calendar. One with no instructor and no substitute says "No instructor".
 * Private lessons, booked in Kalver Scheduler, are explained under
 * their own filter rather than in a footnote the card's height would clip.
 */

import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { formatTime12h, shortDateLabel } from "@/lib/schedule";
import {
  ALL_TYPES,
  meetingPhase,
  needsInstructor,
  nowIn,
  privateLessonsNote,
  todayFilters,
  todayMeetings,
  todayMeetingPath,
  todayTeacher,
} from "@/lib/orgToday";
import { Button, Card, Icon, Tag, TextTabs } from "@/components/studio/primitives";

/** The clock in the organization's zone, moved on every minute so "Now" follows it. */
function useNow(zone) {
  const [now, setNow] = useState(() => nowIn(zone));
  useEffect(() => {
    setNow(nowIn(zone));
    const id = setInterval(() => setNow(nowIn(zone)), 60_000);
    return () => clearInterval(id);
  }, [zone]);
  return now;
}

function TodayRow({ meeting, phase, onOpen }) {
  const teacher = todayTeacher(meeting);
  const quiet = phase === "done" || phase === "cancelled";
  return (
    <li className="border-t border-[var(--studio-divider)]" data-testid="today-meeting">
      <button
        type="button"
        onClick={() => onOpen(todayMeetingPath(meeting))}
        className="flex w-full items-start gap-3 px-5 py-3 text-left transition-colors duration-150 hover:bg-[var(--studio-overlay)]"
      >
        <span
          className={cn(
            "w-[68px] shrink-0 pt-px font-mono text-[13px] tabular-nums",
            quiet ? "text-[var(--text-tertiary)]" : "text-[var(--text-primary)]",
          )}
        >
          {formatTime12h(meeting.time_local)}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block truncate font-sans text-[14px] font-semibold",
              quiet ? "text-[var(--text-tertiary)]" : "text-[var(--text-primary)]",
            )}
          >
            {meeting.class_name || "Class"}
          </span>
          <span className="mt-0.5 block truncate font-sans text-[12px] text-[var(--text-tertiary)]">
            {[meeting.program_title, teacher].filter(Boolean).join(" · ")}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {phase === "now" && <Tag accent className="px-2 py-0.5 text-[11px]">Now</Tag>}
          {phase === "cancelled" && <Tag className="px-2 py-0.5 text-[11px]">No class</Tag>}
          {meeting.substitute_name && phase !== "cancelled" && <Tag className="px-2 py-0.5 text-[11px]">Substitute</Tag>}
          {needsInstructor(meeting) && (
            <Tag accent className="px-2 py-0.5 text-[11px]" data-testid="today-no-instructor">
              No instructor
            </Tag>
          )}
          <Icon name="chevronRight" size={16} className="text-[var(--text-tertiary)]" />
        </span>
      </button>
    </li>
  );
}

/**
 * @param {Object} props
 * @param {string} props.date - Today in the organization's zone ("YYYY-MM-DD").
 * @param {Object|null} props.data - `/org/calendar` for today; `{failed: true}` when the read failed.
 * @param {() => void} props.onRetry
 * @param {(path: string) => void} props.onOpen - Goes to a meeting's class page.
 * @param {string} [props.className]
 */
export default function TodayCard({ date, data, onRetry, onOpen, className }) {
  const [type, setType] = useState(ALL_TYPES);
  const now = useNow(data?.timezone || null);
  const filters = useMemo(() => todayFilters(data), [data]);
  const chosen = filters.some((f) => f.id === type) ? type : ALL_TYPES;
  const meetings = useMemo(() => todayMeetings(data, chosen), [data, chosen]);
  const note = privateLessonsNote(data);
  const loading = !data;
  const failed = Boolean(data?.failed);

  let body;
  if (loading) {
    body = (
      <div className="space-y-2 px-5 pb-5" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton h-11 rounded-xl" />
        ))}
      </div>
    );
  } else if (failed) {
    body = (
      <div className="px-5 pb-5">
        <p className="font-sans text-[14px] text-[var(--text-secondary)]">Today's classes couldn't be loaded.</p>
        <Button variant="ghost" size="sm" icon="refresh" className="mt-3" onPress={onRetry} data-testid="today-retry">
          Try again
        </Button>
      </div>
    );
  } else if (meetings.length === 0) {
    body = (
      <p className="px-5 pb-5 font-sans text-[14px] text-[var(--text-secondary)]" data-testid="today-empty">
        {chosen === "private_lessons" || (note && filters.length === 0)
          ? note
          : data.complete === false
            ? "No classes loaded for today."
            : "No classes today."}
      </p>
    );
  } else {
    body = (
      <ul data-testid="today-list">
        {meetings.map((m) => (
          <TodayRow
            key={m.occurrence_id || m.session_id || `${m.class_id}-${m.time_local}`}
            meeting={m}
            phase={meetingPhase(m, now)}
            onOpen={onOpen}
          />
        ))}
      </ul>
    );
  }

  return (
    <Card padding={0} className={cn("flex flex-col overflow-hidden", className)} data-testid="dashboard-today">
      <div className="flex items-baseline justify-between gap-3 px-5 pb-3 pt-5">
        <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Today</h2>
        {date && <span className="font-sans text-[13px] text-[var(--text-tertiary)]">{shortDateLabel(date)}</span>}
      </div>
      {filters.length > 0 && (
        <TextTabs
          testId="today-filter"
          className="mx-5 mb-1 gap-4 [&>button]:pb-2 [&>button]:text-[13px]"
          value={chosen}
          onChange={setType}
          options={filters.map((f) => ({ ...f, testId: `today-filter-${f.id}` }))}
        />
      )}
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="today-scroll">
        {body}
        {!loading && !failed && data.complete === false && (
          <p role="status" className="border-t border-[var(--studio-divider)] px-5 py-3 font-sans text-[12px] text-[var(--text-tertiary)]">
            Some class schedules are unavailable, so this list may be incomplete.
          </p>
        )}
      </div>
    </Card>
  );
}
